import React, { useState, useEffect } from 'react';
import { BottomSheet, ToggleSwitch } from '../../../shared/ui';
import { questionMemberAddPermissionsApi, QuestionMemberAddPermission } from '../../../api/questionMemberAddPermissionsApi';
import { siteMembersApi } from '../../../api/siteMembersApi';
import { sitesV2Api } from '../../../api/sitesV2Api';
import { siteMemberPermissionsApi, Permission, SiteMemberPermission } from '../../../api/siteMemberPermissionsApi';
import { RoleBadges } from '../../../shared/ui/RoleBadges';
import './ManageMemberPermissions.css';

interface ManageMemberPermissionsProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: number;
  siteMemberId: number;
  userName: string;
  userRole: string;
}

interface Role {
  id: number;
  name: string;
}

interface TypeInfo {
  id: number;
  name: string;
}

const ManageMemberPermissions: React.FC<ManageMemberPermissionsProps> = ({
  isOpen,
  onClose,
  siteId,
  siteMemberId,
  userName,
  userRole
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [types, setTypes] = useState<TypeInfo[]>([
    { id: 1, name: 'Дискуссии' },
    { id: 2, name: 'Заметки' },
    { id: 3, name: 'Снабжение' }
  ]);
  const [permissions, setPermissions] = useState<QuestionMemberAddPermission[]>([]);
  const [viewPermissions, setViewPermissions] = useState<SiteMemberPermission[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [expandedCards, setExpandedCards] = useState<Set<number | string>>(new Set()); // По умолчанию все свернуты
  const errorTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen && siteMemberId) {
      loadPermissions();
    }
  }, [isOpen, siteMemberId, siteId]);

  // Автоматическое скрытие ошибки через 5 секунд
  useEffect(() => {
    if (error) {
      // Очищаем предыдущий таймер, если он есть
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      // Устанавливаем новый таймер
      errorTimeoutRef.current = setTimeout(() => {
        setError(null);
      }, 5000);
    }

    // Cleanup функция
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [error]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Загружаем все доступные права в системе
      const allPermissions = await siteMemberPermissionsApi.getAllPermissions();
      setAvailablePermissions(allPermissions);

      // Загружаем права просмотра участника
      const viewPerms = await siteMemberPermissionsApi.getMemberPermissions(siteId, siteMemberId);
      setViewPermissions(viewPerms);

      // Загружаем права участника на добавление
      let permissionsData = await questionMemberAddPermissionsApi.getAddPermissions(siteId, siteMemberId);

      // Если прав нет, создаем дефолтные
      if (!permissionsData || permissionsData.length === 0) {
        try {
          const members = await siteMembersApi.getActiveSiteMembers(siteId);
          const currentMember = members.find(m => m.id === siteMemberId);
          if (currentMember && currentMember.roleId) {
            await questionMemberAddPermissionsApi.resetToDefault(siteId, siteMemberId, currentMember.roleId);
            permissionsData = await questionMemberAddPermissionsApi.getAddPermissions(siteId, siteMemberId);
          }
        } catch (resetError) {
          console.error('Error creating default permissions:', resetError);
        }
      }

      setPermissions(permissionsData || []);

      // Загружаем список ролей через V2 API
      let rolesData: Role[] = [];
      try {
        const siteRoles = await sitesV2Api.getSiteRoles(siteId);
        rolesData = siteRoles.map(r => ({ id: r.id, name: r.name }));
      } catch (rolesError) {
        console.error('Error loading roles from V2 API:', rolesError);
        try {
          const members = await siteMembersApi.getActiveSiteMembers(siteId);
          rolesData = Array.from(
            new Map(
              members
                .filter(m => m.roleId && m.roleName)
                .map(m => [m.roleId, { id: m.roleId!, name: m.roleName! }])
            ).values()
          );
        } catch (membersError) {
          console.error('Error loading roles from members:', membersError);
        }
      }
      setRoles(rolesData);
    } catch (err: any) {
      console.error('Error loading permissions:', err);
      setError(`Ошибка загрузки прав: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (typeId: number, roleId: number): boolean => {
    return permissions.some(p => p.typeId === typeId && p.allowedRoleId === roleId);
  };

  const togglePermission = async (typeId: number, roleId: number) => {
    try {
      const currentHasPermission = hasPermission(typeId, roleId);
      if (currentHasPermission) {
        setPermissions(prev => prev.filter(p => !(p.typeId === typeId && p.allowedRoleId === roleId)));
      } else {
        setPermissions(prev => [...prev, { 
          id: 0, // Временный ID
          siteMemberId, 
          typeId, 
          allowedRoleId: roleId,
          createdAt: new Date().toISOString()
        }]);
      }

      // В фоне отправляем запрос на сервер
      if (currentHasPermission) {
        await questionMemberAddPermissionsApi.revokeAddPermission(siteId, siteMemberId, roleId, typeId);
      } else {
        await questionMemberAddPermissionsApi.grantAddPermission(siteId, siteMemberId, roleId, typeId);
      }
    } catch (err: any) {
      console.error('Error toggling permission:', err);
      // При ошибке откатываем изменения
      await loadPermissions();
      setError(`Ошибка изменения права: ${err.message}`);
    }
  };

  const getPermissionCount = (typeId: number): number => {
    return permissions.filter(p => p.typeId === typeId).length;
  };

  const toggleCardExpanded = (typeId: number | string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(typeId)) {
        newSet.delete(typeId);
      } else {
        newSet.add(typeId);
      }
      return newSet;
    });
  };

  const isCardExpanded = (typeId: number | string): boolean => {
    return expandedCards.has(typeId);
  };

  const hasViewPermission = (permissionCode: string): boolean => {
    return viewPermissions.some(p => p.permissionCode === permissionCode);
  };

  const toggleViewPermission = async (permissionCode: string) => {
    try {
      const currentHasPermission = hasViewPermission(permissionCode);
      if (currentHasPermission) {
        setViewPermissions(prev => prev.filter(p => p.permissionCode !== permissionCode));
      } else {
        const permission = availablePermissions.find(p => p.code === permissionCode);
        if (permission) {
          setViewPermissions(prev => [...prev, { 
            id: 0, // Временный ID
            siteMemberId,
            permissionId: permission.id,
            permissionCode: permission.code,
            grantedAt: new Date().toISOString()
          }]);
        }
      }

      // В фоне отправляем запрос на сервер
      if (currentHasPermission) {
        await siteMemberPermissionsApi.revokePermission(siteId, siteMemberId, permissionCode);
      } else {
        await siteMemberPermissionsApi.grantPermission(siteId, siteMemberId, permissionCode);
      }
    } catch (err: any) {
      console.error('Error toggling view permission:', err);
      // При ошибке откатываем изменения
      await loadPermissions();
      setError(`Ошибка изменения права: ${err.message}`);
    }
  };

  const getViewPermissionCount = (): number => {
    return viewPermissions.length;
  };

  // Группировка прав по категориям
  const groupPermissionsByCategory = () => {
    const groups: Record<string, Permission[]> = {
      questions: [],
      notes: [],
      supply: [],
      sessions: [],
      other: []
    };

    availablePermissions.forEach(permission => {
      const code = permission.code.toUpperCase();
      
      if (code.startsWith('QUESTION_') || code.includes('DISCUSSION')) {
        groups.questions.push(permission);
      } else if (code.startsWith('NOTE_')) {
        groups.notes.push(permission);
      } else if (code.startsWith('SUPPLY_')) {
        groups.supply.push(permission);
      } else if (code.startsWith('SESSION_')) {
        groups.sessions.push(permission);
      } else {
        groups.other.push(permission);
      }
    });

    return groups;
  };

  const getCategoryTitle = (category: string): string => {
    const titles: Record<string, string> = {
      questions: 'Дискуссии',
      notes: 'Заметки',
      supply: 'Снабжение',
      sessions: 'Смены',
      other: 'Прочие права'
    };
    return titles[category] || category;
  };

  const getCategoryIcon = (category: string): string => {
    const icons: Record<string, string> = {
      questions: 'ri-question-answer-line',
      notes: 'ri-sticky-note-line',
      supply: 'ri-truck-line',
      sessions: 'ri-time-line',
      other: 'ri-settings-3-line'
    };
    return icons[category] || 'ri-checkbox-circle-line';
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={`Настройки прав: ${userName}`}
      size="large"
    >
      <div className="manage-member-permissions">
        <div className="permissions-header">
          <div className="user-info">
            <i className="ri-user-line"></i>
            <div>
              <div className="user-name">{userName}</div>
              <RoleBadges 
                roles={undefined} 
                fallbackRoleName={userRole}
              />
            </div>
          </div>
          <p className="permissions-description">
            Настройте права доступа участника к различным разделам и функциям проекта.
          </p>
        </div>

        {error && (
          <div className="error-message">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <i className="ri-loader-4-line ri-spin"></i>
            <span>Загрузка прав...</span>
          </div>
        ) : (
          <div className="permissions-grid">
            {/* Карточка с правами доступа */}
            <div className={`permission-type-card ${isCardExpanded('view') ? 'expanded' : 'collapsed'}`}>
              <div className="type-header" onClick={() => toggleCardExpanded('view')}>
                <div className="type-header-content">
                  <h4>Права доступа</h4>
                  <span className="permission-count">
                    {getViewPermissionCount()} из {availablePermissions.length}
                  </span>
                </div>
                <button 
                  className="expand-button"
                  type="button"
                  aria-label={isCardExpanded('view') ? 'Свернуть' : 'Развернуть'}
                >
                  <i className={`ri-arrow-${isCardExpanded('view') ? 'up' : 'down'}-s-line`}></i>
                </button>
              </div>
              
              {isCardExpanded('view') && (
                <div className="permissions-categories">
                  {Object.entries(groupPermissionsByCategory()).map(([category, permissions]) => {
                    if (permissions.length === 0) return null;
                    
                    return (
                      <div key={category} className="permission-category">
                        <div className="category-header">
                          <i className={getCategoryIcon(category)}></i>
                          <span className="category-title">{getCategoryTitle(category)}</span>
                        </div>
                        <div className="category-permissions">
                          {permissions.map(permission => (
                            <ToggleSwitch
                              key={permission.code}
                              checked={hasViewPermission(permission.code)}
                              onChange={() => toggleViewPermission(permission.code)}
                              label={permission.description || permission.code}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Карточки с правами на добавление участников */}
            {types.map(type => {
              const expanded = isCardExpanded(type.id);
              return (
                <div key={type.id} className={`permission-type-card ${expanded ? 'expanded' : 'collapsed'}`}>
                  <div className="type-header" onClick={() => toggleCardExpanded(type.id)}>
                    <div className="type-header-content">
                      <h4>{type.name} (добавление участников)</h4>
                      <span className="permission-count">
                        {getPermissionCount(type.id)} из {Math.max(roles.length, getPermissionCount(type.id))}
                      </span>
                    </div>
                    <button 
                      className="expand-button"
                      type="button"
                      aria-label={expanded ? 'Свернуть' : 'Развернуть'}
                    >
                      <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line`}></i>
                    </button>
                  </div>
                  
                  {expanded && (
                    <div className="roles-list">
                      {roles.map(role => (
                        <ToggleSwitch
                          key={role.id}
                          checked={hasPermission(type.id, role.id)}
                          onChange={() => togglePermission(type.id, role.id)}
                          label={role.name}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="permissions-footer">
          <button className="close-button" onClick={onClose}>
            <i className="ri-check-line"></i>
            Готово
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default ManageMemberPermissions;

